import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, of, filter, tap } from 'rxjs';
import { catchError, map, mergeMap, withLatestFrom } from 'rxjs/operators';
import { Store } from '@ngrx/store';

import {
  CLIENT_FIND,
  CLIENT_FIND_SUCCESS,
  CLIENT_FAILED,
  CLIENT_UPDATE_LOADING,
  NOTIFICATION_SHOW,
  CLIENT_REMOVE_ADDRESS,
  CLIENT_CREATE_ADDRESS,
  CLIENT_UPDATE_ADDRESS,
  CLIENT_ADDRESS_CREATE_COMPLETED,
  CLIENT_ADDRESS_UPDATE_COMPLETED,
  CLIENT_FIND_LOCATION,
  CLIENT_FIND_LOCATION_SUCCESS,
  CLIENT_ADDRESS_UPDATE_DEFAULT,
  CLIENT_UPDATE,
  AUTH_REFRESH_TOKEN,
} from '../actions';

import {
  selectAuth,
  selectLocalChannelId,
  selectClientAddressForm,
  selectClientAddressId,
  selectUserForm,
} from '../selectors';

import { ClientService } from '@core/services';
import { Address, Client, ClientUserForm } from '@core/models';
import { GeolocationService } from '@core/services/geolocation/geolocation.service';
import { NotificationEnum } from '@shared/components/notification/enum/notification.enum';

@Injectable()
export class ClientEffects {
  localChannelId$: Observable<string | null>;

  constructor(
    private actions$: Actions,
    private clientService: ClientService,
    private store: Store,
    private geolocationService: GeolocationService
  ) {
    this.localChannelId$ = this.store.select(selectLocalChannelId);
  }

  creationComplete$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CLIENT_ADDRESS_CREATE_COMPLETED),
        tap(() => {
          this.clientService.creationCompleted();
        })
      ),
    { dispatch: false }
  );

  updateComplete$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CLIENT_ADDRESS_UPDATE_COMPLETED),
        tap(() => {
          this.clientService.updateCompleted();
        })
      ),
    { dispatch: false }
  );

  find$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CLIENT_FIND),
      withLatestFrom(this.store.select(selectAuth)),
      filter(([_, client]) => !!client && !!client?.clienteId),
      map(([_, client]) => {
        return { client };
      }),
      
      mergeMap(({ client }) => {
        this.store.dispatch(CLIENT_UPDATE_LOADING({ loading: true }));
        return this.clientService.find(client).pipe(
          mergeMap(({ data: response }) => {
            const [client] = response as Client[];
            return [
              CLIENT_UPDATE_LOADING({ loading: false }),
              CLIENT_FIND_SUCCESS({ client }),
              // ? TO DO: NOT USED OR IRRELEVANT
              // AUTH_FIND_SUCCESS({ auth: data }),
            ];
          }),
          catchError(error =>
            of(
              CLIENT_FAILED({
                error: error.error?.message || 'Error loading order types',
              })
            )
          )
        );
      })
    )
  );

  findLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CLIENT_FIND_LOCATION),
      mergeMap(({}) => {
        return this.geolocationService.getUserLocation().pipe(
          mergeMap(data => {
            return [CLIENT_FIND_LOCATION_SUCCESS({ location: data }), CLIENT_UPDATE_LOADING({ loading: false })];
          }),
          catchError(error =>
            of(
              CLIENT_FAILED({
                error: error.error?.message || 'Error getting user location',
              })
            )
          )
        );
      })
    )
  );

  remove$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CLIENT_REMOVE_ADDRESS),
      filter(action => !!action.idAddress),
      map(action => {
        return { addressId: action.idAddress as string };
      }),

      mergeMap(({ addressId }) => {
        this.store.dispatch(CLIENT_UPDATE_LOADING({ loading: true }));
        return this.clientService.remove(addressId).pipe(
          mergeMap(() => {
            return [
              AUTH_REFRESH_TOKEN(),
              CLIENT_UPDATE_LOADING({ loading: false }),
              NOTIFICATION_SHOW({
                notification: {
                  title: NotificationEnum.RemoveAddress,
                  type: NotificationEnum.Success,
                  detail: NotificationEnum.ClientAddressRemoveSuccessfully,
                },
              }),
            ];
          }),
          catchError(error => {
            return [
              CLIENT_UPDATE_LOADING({ loading: false }),
              NOTIFICATION_SHOW({
                notification: {
                  title: NotificationEnum.RemoveAddressFailure,
                  type: NotificationEnum.Error,
                  detail: error.error.message,
                },
              }),
              CLIENT_FAILED({
                error: error.error?.message || 'Error loading order types',
              }),
            ];
          })
        );
      })
    )
  );

  create$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CLIENT_CREATE_ADDRESS),
      withLatestFrom(this.store.select(selectClientAddressForm), this.store.select(selectAuth)),
      filter(([_, clientAddressForm, auth]) => !!clientAddressForm && !!auth),
      map(([_, clientAddressForm, auth]) => {
        return { clientAddressForm: clientAddressForm as Address, clientId: auth?.clienteId as string };
      }),
      mergeMap(({ clientAddressForm, clientId }) => {
        this.store.dispatch(CLIENT_UPDATE_LOADING({ loading: true }));
        return this.clientService.create(clientAddressForm, clientId).pipe(
          mergeMap(() => {
            return [
              // CLIENT_ADDRESS_UPDATE_DEFAULT({idAddress: }),
              AUTH_REFRESH_TOKEN(),
              CLIENT_UPDATE_LOADING({ loading: false }),
              CLIENT_ADDRESS_CREATE_COMPLETED(),
              NOTIFICATION_SHOW({
                notification: {
                  title: NotificationEnum.CreateAddress,
                  type: NotificationEnum.Success,
                  detail: NotificationEnum.ClientAddressCreateSuccessfully,
                },
              }),
            ];
          }),
          catchError(error => {
            this.store.dispatch(CLIENT_UPDATE_LOADING({ loading: false }));
            return of(
              NOTIFICATION_SHOW({
                notification: {
                  title: NotificationEnum.CreateAddressFailure,
                  type: NotificationEnum.Error,
                  detail: error.error?.message || NotificationEnum.ErrorCreatingAddress,
                },
              })
            );
          })
        );
      })
    )
  );

  update$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CLIENT_UPDATE),
      withLatestFrom(this.store.select(selectUserForm), this.store.select(selectAuth)),
      filter(([_, userForm, auth]) => {

        console.log(_)


        console.log(userForm)

        console.log(auth)

        
        return !!userForm && !!auth}
      ),
      map(([_, userForm, auth]) => {
        return { userForm: userForm as unknown as ClientUserForm, clientId: auth?.clienteId as string };
      }),
      mergeMap(({ userForm, clientId }) => {
        this.store.dispatch(CLIENT_UPDATE_LOADING({ loading: true }));
        return this.clientService.update(userForm, clientId).pipe(
          mergeMap(() => {
            return [
              AUTH_REFRESH_TOKEN(),
              CLIENT_UPDATE_LOADING({ loading: false }),
              CLIENT_ADDRESS_UPDATE_COMPLETED(),
              NOTIFICATION_SHOW({
                notification: {
                  title: NotificationEnum.UpdateUser,
                  type: NotificationEnum.Success,
                  detail: NotificationEnum.UserUpdateSuccessfully,
                },
              }),
            ];
          }),
          catchError(error =>
            of(
              NOTIFICATION_SHOW({
                notification: {
                  title: NotificationEnum.Error,
                  type: NotificationEnum.Error,
                  detail: error.error?.message || NotificationEnum.ErrorUpdatingUser,
                },
              }),
              CLIENT_FAILED({
                error: error.error?.message || 'Error updating user',
              })
            )
          )
        );
      })
    )
  );

  updateAddress$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CLIENT_UPDATE_ADDRESS),
      withLatestFrom(this.store.select(selectClientAddressForm), this.store.select(selectClientAddressId)),
      filter(([_, clientAddressForm, idAddress]) => !!clientAddressForm && !!idAddress),
      map(([_, clientAddressForm, idAddress]) => {
        return { clientAddressForm: clientAddressForm as Address, idAddress: idAddress as string };
      }),
      mergeMap(({ clientAddressForm, idAddress }) => {
        this.store.dispatch(CLIENT_UPDATE_LOADING({ loading: true }));
        return this.clientService.updateAddress(clientAddressForm, idAddress).pipe(
          mergeMap(() => {
            return [
              AUTH_REFRESH_TOKEN(),
              CLIENT_UPDATE_LOADING({ loading: false }),
              CLIENT_ADDRESS_CREATE_COMPLETED(),
              NOTIFICATION_SHOW({
                notification: {
                  title: NotificationEnum.CreateAddress,
                  type: NotificationEnum.Success,
                  detail: NotificationEnum.ClientAddressUpdateSuccessfully,
                },
              }),
            ];
          }),
          catchError(error =>
            of(
              CLIENT_FAILED({
                error: error.error?.message || 'Error loading order types',
              })
            )
          )
        );
      })
    )
  );

  updateDefault$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CLIENT_ADDRESS_UPDATE_DEFAULT),
      filter((action) => !!action.idAddress),
      map((action) => {
        return { idAddress: action.idAddress as string };
      }),
      mergeMap(({ idAddress }) => {
        return this.clientService.updateDefault(idAddress).pipe(
          mergeMap(() => {
            return [CLIENT_UPDATE_LOADING({ loading: false })];
          }),
          catchError(error =>
            of(
              CLIENT_FAILED({
                error: error.error?.message || 'Error updating default address',
              })
            )
          )
        );
      })
    )
  );
}
